// Shared: Discord avatar + name + optional in-game handle (tooltip) + org rank icon.
// Use in chat sidebar, calendar officers, roster-style rows, etc.

import React, { useCallback, useMemo, useRef, useState } from "react";
import defaultAvatarImg from "../../medias/avatar_default_medium.jpg";
import MemberProfileCardPopover from "./memberProfileCard/MemberProfileCardPopover";
import { sanitizeColorForDataAttr } from "./chipColorDataAttr";
import useRankChipCspStyles from "../../hooks/useRankChipCspStyles";

/**
 * @param {string|null|undefined} discordAvatar
 * @param {string|null|undefined} discordName
 * @param {string|null|undefined} handle — in-game handle; shown as native tooltip on the name
 * @param {string|null|undefined} rankImageUrl — from OrgRanks via UserRankHistory
 * @param {null|{ chipColor?: string|null, text?: string|null }} [rankColors] — OrgRanksColors; chipColor/text passed as data-chip-bg / data-chip-fg (sanitized)
 * @param {string} [nameFallback] — when discordName is empty
 * @param {string} [className] — extra class on root
 * @param {string} [defaultAvatarSrc] — override default broken-avatar image
 * @param {boolean} [compact] — slightly smaller avatar (e.g. dense lists)
 * @param {boolean} [hideAvatar] — no avatar/placeholder (e.g. header: name + rank only)
 * @param {boolean} [rankBeforeName] — rank icon to the left of the name (e.g. header)
 * @param {number|string|null} [profileCardUserId] — when set, the whole identity row is a chip-style control that opens the member profile card
 * @param {boolean} [profileCardAvatarOnly] — with profileCardUserId: chip contains only the avatar (e.g. overlapping participant avatars on calendar)
 * @param {function|object|null} [dragSetNodeRef] — dnd-kit setNodeRef merged onto the profile chip
 * @param {object|null} [dragListeners] — dnd-kit drag listeners for the profile chip
 * @param {object|null} [dragAttributes] — dnd-kit drag attributes for the profile chip
 * @param {boolean} [isDragging] — dims the source chip while DragOverlay follows the cursor
 * @param {boolean} [plannerTapSelect] — parent handles tap-to-select via drag listeners; double-click opens profile
 */
export default function UserDiscordRankDisplay({
  discordAvatar,
  discordName,
  handle: gameHandleForTooltip,
  rankImageUrl,
  rankColors = null,
  nameFallback = "",
  className = "",
  defaultAvatarSrc,
  compact = false,
  hideAvatar = false,
  rankBeforeName = false,
  profileCardUserId = null,
  profileCardAvatarOnly = false,
  dragSetNodeRef = null,
  dragListeners = null,
  dragAttributes = null,
  isDragging = false,
  plannerTapSelect = false,
}) {
  const fallbackSrc = defaultAvatarSrc ?? defaultAvatarImg;
  const profileChipRef = useRef(null);
  const [profileOpen, setProfileOpen] = useState(false);

  const onAvatarError = (e) => {
    e.currentTarget.onerror = null;
    e.currentTarget.src = fallbackSrc;
  };

  const displayName = (discordName && String(discordName).trim()) || nameFallback || "";
  const handleStr = gameHandleForTooltip != null ? String(gameHandleForTooltip).trim() : "";

  const uidNum =
    profileCardUserId != null && profileCardUserId !== ""
      ? Number(profileCardUserId)
      : NaN;
  const profileEnabled = Number.isInteger(uidNum) && uidNum > 0;

  const chipDataAttrs = useMemo(() => {
    const bg = sanitizeColorForDataAttr(rankColors?.chipColor);
    const fg = sanitizeColorForDataAttr(rankColors?.text);
    const attrs = {};
    if (bg) attrs["data-chip-bg"] = bg;
    if (fg) attrs["data-chip-fg"] = fg;
    return attrs;
  }, [rankColors]);

  useRankChipCspStyles(profileEnabled ? chipDataAttrs["data-chip-bg"] : null);

  const toggleProfile = useCallback(
    (e) => {
      if (!profileEnabled) return;
      e.preventDefault();
      e.stopPropagation();
      setProfileOpen((v) => !v);
    },
    [profileEnabled]
  );

  const openProfileFromDoubleClick = useCallback(
    (e) => {
      if (!profileEnabled || !plannerTapSelect) return;
      e.preventDefault();
      e.stopPropagation();
      toggleProfile(e);
    },
    [profileEnabled, plannerTapSelect, toggleProfile]
  );

  const stopDown = useCallback(
    (e) => {
      if (!profileEnabled || plannerTapSelect) return;
      e.stopPropagation();
    },
    [profileEnabled, plannerTapSelect]
  );

  const rootClass = [
    "user-discord-rank",
    compact ? "user-discord-rank--compact" : "",
    hideAvatar ? "user-discord-rank--no-avatar" : "",
    rankBeforeName ? "user-discord-rank--rank-before-name" : "",
    profileEnabled && profileCardAvatarOnly ? "user-discord-rank--profile-chip-avatar-only" : "",
    className.trim()
  ]
    .filter(Boolean)
    .join(" ");

  const rankIcon = rankImageUrl ? (
    <img src={rankImageUrl} alt="" className="user-discord-rank__rank-icon" />
  ) : null;

  const nameEl = (
    <span
      className="user-discord-rank__name"
      title={handleStr ? handleStr : undefined}
    >
      {displayName}
    </span>
  );

  const avatarVisual =
    !hideAvatar &&
    (discordAvatar ? (
      <img
        src={discordAvatar}
        alt=""
        className="user-discord-rank__avatar"
        onError={onAvatarError}
      />
    ) : (
      <span
        className="user-discord-rank__avatar user-discord-rank__avatar--placeholder"
        aria-hidden
      />
    ));

  const nameAndRank =
    !profileCardAvatarOnly &&
    (rankBeforeName ? (
      <>
        {rankIcon}
        {nameEl}
      </>
    ) : (
      <>
        {nameEl}
        {rankIcon}
      </>
    ));

  if (profileEnabled) {
    const chipClass = [
      rootClass,
      "user-discord-rank--profile-chip",
      profileOpen ? "user-discord-rank--profile-chip-open" : "",
      dragListeners ? "user-discord-rank--draggable" : "",
      isDragging ? "is-dragging" : "",
    ]
      .filter(Boolean)
      .join(" ");

    const setChipRef = (node) => {
      profileChipRef.current = node;
      if (typeof dragSetNodeRef === "function") dragSetNodeRef(node);
    };

    return (
      <>
        <button
          type="button"
          className={chipClass}
          ref={setChipRef}
          {...chipDataAttrs}
          {...(dragAttributes || {})}
          {...(dragListeners || {})}
          onClick={plannerTapSelect ? undefined : toggleProfile}
          onDoubleClick={plannerTapSelect ? openProfileFromDoubleClick : undefined}
          onMouseDown={stopDown}
          aria-haspopup="dialog"
          aria-expanded={profileOpen}
          title={profileCardAvatarOnly && displayName ? displayName : undefined}
          aria-label={`Profile: ${displayName || "member"}`}
        >
          {profileCardAvatarOnly ? avatarVisual : (
            <>
              {avatarVisual}
              {nameAndRank}
            </>
          )}
        </button>
        <MemberProfileCardPopover
          open={profileOpen}
          anchorRef={profileChipRef}
          onClose={() => setProfileOpen(false)}
          userId={uidNum}
          preview={{
            discordAvatar,
            discordName,
            handle: handleStr || null
          }}
        />
      </>
    );
  }

  return (
    <span className={rootClass}>
      {avatarVisual}
      {rankBeforeName ? (
        <>
          {rankIcon}
          {nameEl}
        </>
      ) : (
        <>
          {nameEl}
          {rankIcon}
        </>
      )}
    </span>
  );
}
